<template>
    <NoteText text="You can make notes on the form below" />
    <div class="note">C = Correct, I = Incorrect, R = Repeat, Q = Query</div>

    <!-- Transcription status indicator -->
    <div v-if="isTranscribing" class="transcription-status">
        <div class="transcribing-indicator">Transcribing audio...</div>
    </div>
    <div v-if="transcriptionError" class="transcription-error">
        {{ transcriptionError }}
    </div>

    <div v-if="jsonData" class="text-input-grid">
        <div
          v-for="(item, index) in jsonData.entries"
          :key="item.entryId"
          class="input-row"
        >
          <div class="input-row-main">
            <span class="input-number">{{ index + 1 }}.</span>

            <div class="input-container">
              <div
                class="line-input"
                :class="{ flagged: item.flagged || item.pendingRequest }"
              >
                <div class="word-field">
                  <TextInputWithToggle 
                    :text="item.enteredWord"
                    :selectedOption="item.selectedAnswer"
                    @update:text="updateEnteredWord(index, $event)"
                    @update:selectedOption="updateSelectedAnswer(index, $event)"
                    @focus="handleInputFocus(index)"
                    @blur="handleInputBlur(index)"
                    @keydown="handleKeyDown($event, index)"
                    ref="textInputs"
                  />
                  <!-- AI badge when suggestion applied -->
                  <span v-if="isSuggestionApplied(index)" class="ai-badge">AI</span>

                  <!-- Change history button -->
                  <button
                    v-if="item.changeLog && item.changeLog.length"
                    class="history-btn"
                    :title="`${item.changeLog.length} change(s)`"
                    @click="toggleHistory(index)"
                  >
                    {{ item.changeLog.length }} ✎
                  </button>
                </div>

                <!-- Per-item comment bubble (inline comments for this entry) -->
                <button
                  type="button"
                  class="item-comment-button"
                  :class="{ 'has-comments': item.commentCount > 0 }"
                  title="Item comments"
                  @click="toggleComments(index)"
                >
                  <span class="comment-icon" aria-hidden="true">💬</span>
                  <span
                    v-if="item.commentCount > 0"
                    class="comment-count"
                  >{{ item.commentCount }}</span>
                </button>

                <!-- Flag button to start a change request -->
                <button
                  type="button"
                  class="item-comment-button flag-button"
                  :class="{ 'is-flagged': item.flagged, 'is-posted': item.pendingRequest }"
                  :title="item.pendingRequest
                    ? 'Change request open'
                    : item.flagged
                      ? 'Cancel flag'
                      : 'Request score change'"
                  @click="toggleFlag(index)"
                >
                  <span>{{ (item.pendingRequest || item.flagged) ? '⚑' : '⚐' }}</span>
                </button>
              </div>

              <!-- Change history panel -->
              <div
                v-if="expandedHistoryIndex === index && item.changeLog && item.changeLog.length"
                class="history-panel"
              >
                <div class="history-title">Change history</div>
                <div
                  v-for="h in item.changeLog"
                  :key="h.id"
                  class="history-item"
                >
                  <span
                    class="history-dot"
                    :style="{ background: h.actorColor }"
                  ></span>
                  <span class="history-actor">{{ h.actor }}</span>
                  <span class="history-change">
                    <span class="mark-pill" :class="h.from">{{ h.from || '—' }}</span>
                    →
                    <span class="mark-pill" :class="h.to">{{ h.to || '—' }}</span>
                  </span>
                  <span class="history-reason">{{ h.reason }}</span>
                  <span class="history-time">{{ h.time }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Inline item comment thread -->
          <div v-if="expandedCommentIndex === index" class="item-comment-panel-wrap">
            <div class="item-comment-panel">
              <div class="mini-thread-header">
                <span>Comments on "{{ item.enteredWord || '—' }}"</span>
                <button class="mini-close" @click="expandedCommentIndex = null">Close</button>
              </div>
              <div v-if="item.itemComments.length === 0" class="mini-empty">No comments yet.</div>
              <div
                v-for="c in item.itemComments"
                :key="c.id"
                class="mini-comment"
                :class="{ 'mini-request': c.isRequest }"
              >
                <div
                  class="mini-avatar"
                  :style="{ background: c.avatarColor }"
                >{{ c.initials }}</div>
                <div class="mini-body">
                  <div class="mini-meta">
                    <span class="mini-author">{{ c.author }}</span>
                    <span
                      v-if="c.isRequest"
                      class="mini-request-badge"
                    >⚑ Change request</span>
                    <span class="mini-time">{{ c.time }}</span>
                  </div>
                  <div class="mini-text">{{ c.text }}</div>
                  <div
                    v-if="c.isRequest && (c.currentMark || c.suggestedMark)"
                    class="mini-marks"
                  >
                    <span class="mark-chip">Was: {{ c.currentMark || '—' }}</span>
                    <span
                      v-if="c.suggestedMark"
                      class="mark-chip suggested"
                    >Suggested: {{ c.suggestedMark }}</span>
                    <template v-if="!c.resolved">
                      <button class="accept-btn" @click="acceptRequest(c)">✓ Accept</button>
                      <button class="reject-btn" @click="rejectRequest(c)">✕ Reject</button>
                    </template>
                    <span
                      v-else
                      class="mark-chip resolved"
                    >
                      {{ c.resolvedAction === 'Accepted' ? '✓' : '✕' }}
                      {{ c.resolvedAction }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="mini-new">
                <textarea
                  v-model="item.newComment"
                  class="mini-input"
                  placeholder="Add a comment..."
                  rows="2"
                ></textarea>
                <button
                  class="mini-submit"
                  :disabled="!item.newComment.trim()"
                  @click="postComment(index)"
                >
                  Post
                </button>
              </div>
            </div>
          </div>

          <!-- Inline change-request composer -->
          <div
            v-if="item.flagged && !item.pendingRequest"
            class="request-form"
          >
            <div class="request-form-row">
              <span class="request-label">Suggest change to:</span>
              <div class="toggle small">
                <div
                  v-for="opt in toggleOptions"
                  :key="opt.value"
                  class="option"
                  :class="{ active: item.suggestedMark === opt.value }"
                  @click="setSuggestedMark(index, opt.value)"
                >
                  {{ opt.label }}
                </div>
              </div>
              <span class="request-divider">|</span>
              <span class="request-label">Reason:</span>
              <input
                v-model="item.requestNote"
                class="request-input"
                placeholder="Brief reason..."
                type="text"
              />
              <button
                class="request-submit"
                :disabled="!item.requestNote.trim()"
                @click="postRequest(index)"
              >
                Post
              </button>
              <button class="request-cancel" @click="cancelFlag(index)">Cancel</button>
            </div>
          </div>
        </div>
    </div>
    
    <div class="summary-section">
        <h3>Entry Summary</h3>
        <div class="summary-grid">
            <div class="summary-item">
                <span class="summary-label">Correct (C):</span>
                <span class="summary-count">{{ getSummaryValue('c') }}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">Incorrect (I):</span>
                <span class="summary-count">{{ getSummaryValue('i') + getSummaryValue('ir') }}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">Repeat (R):</span>
                <span class="summary-count">{{ getSummaryValue('r') + getSummaryValue('ir')}}</span>
            </div>
            <div class="summary-item">
                <span class="summary-label">Query (Q):</span>
                <span class="summary-count">{{ getSummaryValue('q') }}</span>
            </div>
            <div v-if="openRequestCount > 0" class="summary-item">
                <span class="summary-label open-requests">⚑ Open requests:</span>
                <span class="summary-count open-requests">{{ openRequestCount }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import TextInputWithToggle from '../TextInputWithToggle.vue';
import NoteText from '../NoteText.vue';
import OpenAIService from '../../services/OpenAIService';

export default {
    components: {
        TextInputWithToggle,
        NoteText
    },
    props: {
        jsonData: {
            type: Object,
            default: null
        },
        audioFile: {
            type: Object,
            default: null
        }
    },
    emits: ['update:enteredWord', 'update:selectedAnswer', 'entrySummaryUpdated', 'update:suggestedWords', 'taskDataChanged'],
    data() {
        return {
            isTranscribing: false,
            transcriptionError: null,
            transcriptionSuggestions: [],
            appliedSuggestions: new Set(),
            originalSuggestions: new Map(),
            focusedIndex: null,
            typingIndices: new Set(), // Track which inputs have user typing
            typingTimeouts: {}, // Track typing timeouts
            expandedCommentIndex: null,
            expandedHistoryIndex: null,
            nextId: 200,
            currentUser: {
                id: 1,
                name: 'Interviewer',
                initials: 'IN',
                color: '#317391'
            },
            toggleOptions: [
                { label: 'C', value: 'c' },
                { label: 'I', value: 'i' },
                { label: 'R', value: 'r' },
                { label: 'Q', value: 'q' }
            ],
            taskComments: []
        };
    },
    watch: {
        // Watch for changes to the audioFile prop
        audioFile: {
            immediate: true,
            handler(newFile) {
                if (newFile) {
                    this.transcribeAudioFile(newFile);
                }
            }
        },
        // Watch for changes to jsonData to load suggestions
        jsonData: {
            immediate: true,
            handler(newData) {
                if (newData && newData.suggestedWords && newData.suggestedWords.length > 0) {
                    this.loadSuggestionsFromData(newData.suggestedWords);
                }
                if (newData && Array.isArray(newData.entries)) {
                    newData.entries.forEach((e) => {
                        if (e.flagged === undefined) e.flagged = false;
                        if (e.pendingRequest === undefined) e.pendingRequest = false;
                        if (e.suggestedMark === undefined) e.suggestedMark = null;
                        if (e.requestNote === undefined) e.requestNote = '';
                        if (e.commentCount === undefined) e.commentCount = 0;
                        if (!Array.isArray(e.itemComments)) e.itemComments = [];
                        if (e.newComment === undefined) e.newComment = '';
                        if (!Array.isArray(e.changeLog)) e.changeLog = [];
                    });
                }
            }
        }
    },
    mounted() {
        // Load suggestions from jsonData if available
        if (this.jsonData && this.jsonData.suggestedWords && this.jsonData.suggestedWords.length > 0) {
            this.loadSuggestionsFromData(this.jsonData.suggestedWords);
        }

        // Resolve current user from Cognito so change history shows username
        this.resolveCurrentUser();
    },
    methods: {
        notifyTaskDataChanged() {
            this.$emit('taskDataChanged');
        },
        // Load suggestions from saved data
        loadSuggestionsFromData(words) {
            if (Array.isArray(words) && words.length > 0) {
                this.transcriptionSuggestions = words;
                console.log('Loaded suggestions from data:', words);
                
                // Reset applied suggestions when loading new data
                this.appliedSuggestions = new Set();
                this.originalSuggestions = new Map();
                
                // We don't automatically mark entries as suggestions anymore
                // Let the user explicitly apply suggestions via tab
            }
        },
        async resolveCurrentUser() {
            try {
                const { CognitoUserPool } = await import('amazon-cognito-identity-js');
                const poolData = {
                    UserPoolId: 'us-east-2_3novul0pN',
                    ClientId: '51v76uda8o5l6niokhg852qnj5'
                };
                const userPool = new CognitoUserPool(poolData);
                const cognitoUser = userPool.getCurrentUser();

                if (!cognitoUser) {
                    return;
                }

                cognitoUser.getSession((err, session) => {
                    if (err || !session || !session.isValid()) {
                        return;
                    }
                    const idToken = session.getIdToken();
                    const payload = idToken.payload || {};
                    const username = payload['cognito:username'] || payload.username || 'Interviewer';

                    this.currentUser = {
                        ...this.currentUser,
                        name: username,
                        initials: (username || 'IN')
                            .split(' ')
                            .map((p) => p.charAt(0))
                            .join('')
                            .toUpperCase()
                            .slice(0, 3)
                    };
                });
            } catch (e) {
                // Fallback silently to default currentUser
                // eslint-disable-next-line no-console
                console.warn('Unable to resolve Cognito user for change history', e);
            }
        },
        updateEnteredWord(index, text) {
            if (this.jsonData && this.jsonData.entries && this.jsonData.entries[index]) {
                // Check if this was a suggestion that's now being modified
                if (this.appliedSuggestions.has(index)) {
                    const originalSuggestion = this.originalSuggestions.get(index);
                    // If the text is different from the original suggestion, remove the badge
                    if (text !== originalSuggestion) {
                        this.appliedSuggestions.delete(index);
                    }
                }
                
                // Emit the update event instead of directly modifying the prop
                this.$emit('update:enteredWord', index, text);
                
                // Mark this index as typing
                this.typingIndices.add(index);
                
                // Clear typing status after a delay
                clearTimeout(this.typingTimeouts[index]);
                this.typingTimeouts[index] = setTimeout(() => {
                    this.typingIndices.delete(index);
                }, 500);
            }
        },
        updateSelectedAnswer(index, value) {
            if (this.jsonData && this.jsonData.entries && this.jsonData.entries[index]) {
                const entry = this.jsonData.entries[index];
                const prev = entry.selectedAnswer;
                const next = value;
                if (prev !== next) {
                    if (!Array.isArray(entry.changeLog)) entry.changeLog = [];
                    entry.changeLog.push({
                        id: `h_${this.nextId++}`,
                        actor: this.currentUser.name,
                        actorColor: this.currentUser.color,
                        from: prev ? prev.toUpperCase() : '—',
                        to: next ? next.toUpperCase() : '—',
                        reason: 'Manual edit',
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    });
                }
            }
            this.$emit('update:selectedAnswer', index, value);
        },
        toggleComments(index) {
            this.expandedCommentIndex = this.expandedCommentIndex === index ? null : index;
        },
        toggleHistory(index) {
            this.expandedHistoryIndex = this.expandedHistoryIndex === index ? null : index;
        },
        getSummaryValue(key) {
            if (this.jsonData && this.jsonData.entrySummary) {
                return this.jsonData.entrySummary[key] || 0;
            }
            return 0;
        },
        openRequestCount() {
            if (!this.jsonData || !Array.isArray(this.jsonData.entries)) return 0;
            return this.jsonData.entries.reduce((n, e) => {
                if (!Array.isArray(e.itemComments)) return n;
                return n + e.itemComments.filter((c) => c.isRequest && !c.resolved).length;
            }, 0);
        },
        // Transcribe the audio file automatically when it's provided
        async transcribeAudioFile(file) {
            if (!file) return;
            
            this.isTranscribing = true;
            this.transcriptionError = null;
            
            try {
                const transcriptionText = await OpenAIService.transcribeAudio(file);
                console.log('Transcription result:', transcriptionText);
                
                // Process the transcription into individual words
                this.processTranscription(transcriptionText);
            } catch (error) {
                console.error('Transcription error:', error);
                this.transcriptionError = `Error transcribing audio: ${error.message || 'Unknown error'}`;
            } finally {
                this.isTranscribing = false;
            }
        },
        // Process the transcription text into individual words
        processTranscription(text) {
            if (!text) return;
            
            // Split the text into words, clean them, and filter out empty strings
            const words = text.split(/\s+/)
                .map(word => word.trim().toLowerCase())
                .filter(word => word.length > 0);
            
            console.log('Processed words:', words);
            
            // Store the words as suggestions
            this.transcriptionSuggestions = words;
            
            // Reset applied suggestions
            this.appliedSuggestions = new Set();
            this.typingIndices = new Set();
            
            // Emit event to update suggestedWords in parent component
            this.$emit('update:suggestedWords', words);
        },
        // Check if there's a suggestion for a specific index
        hasSuggestion(index) {
            // Don't show suggestion if:
            // 1. There's no suggestion at this index, or
            // 2. User is currently typing, or
            // 3. The input already has text entered
            return index < this.transcriptionSuggestions.length && 
                   this.transcriptionSuggestions[index] && 
                   !this.typingIndices.has(index) &&
                   this.jsonData && 
                   this.jsonData.entries && 
                   this.jsonData.entries[index] && 
                   !this.jsonData.entries[index].enteredWord;
        },
        // Get the suggestion for a specific index
        getSuggestion(index) {
            return this.transcriptionSuggestions[index] || '';
        },
        // Check if a suggestion has been applied
        isSuggestionApplied(index) {
            // Only return true if the suggestion has been explicitly applied
            // from the transcription suggestions, not just if there's text
            return this.appliedSuggestions.has(index);
        },
        // Apply a suggestion to an entry
        applySuggestion(index) {
            if (this.hasSuggestion(index) && !this.isSuggestionApplied(index)) {
                const suggestion = this.getSuggestion(index);
                this.updateEnteredWord(index, suggestion);
                this.appliedSuggestions.add(index);
                // Store the original suggestion text
                this.originalSuggestions.set(index, suggestion);
            }
        },
        // Handle input focus
        handleInputFocus(index) {
            this.focusedIndex = index;
        },
        // Handle input blur
        handleInputBlur(index) {
            if (this.focusedIndex === index) {
                this.focusedIndex = null;
            }
        },
        // Handle key down events
        handleKeyDown(event, index) {
            // If Tab key is pressed and there's a suggestion
            if (event.key === 'Tab' && this.hasSuggestion(index) && !this.isSuggestionApplied(index)) {
                event.preventDefault(); // Prevent default tab behavior
                this.applySuggestion(index);
                
                // Focus the next input if available
                this.$nextTick(() => {
                    const nextIndex = index + 1;
                    if (this.$refs.textInputs && this.$refs.textInputs[nextIndex]) {
                        this.$refs.textInputs[nextIndex].focus();
                    }
                });
            }
        },
        // Handle suggestion click
        handleSuggestionClick(index) {
            // Focus the input
            if (this.$refs.textInputs && this.$refs.textInputs[index]) {
                this.$refs.textInputs[index].focus();
            }
        },
        truncateSuggestion(suggestion) {
            if (!suggestion) return '';
            // Limit suggestion to a reasonable length (adjust as needed)
            const maxLength = 25;
            return suggestion.length > maxLength 
                ? suggestion.substring(0, maxLength) + '...' 
                : suggestion;
        },
        setSuggestedMark(index, value) {
            if (!this.jsonData || !this.jsonData.entries || !this.jsonData.entries[index]) return;
            const entry = this.jsonData.entries[index];
            entry.suggestedMark = entry.suggestedMark === value ? null : value;
        },
        toggleFlag(index) {
            if (!this.jsonData || !this.jsonData.entries || !this.jsonData.entries[index]) return;
            const entry = this.jsonData.entries[index];
            if (entry.pendingRequest) return;
            entry.flagged = !entry.flagged;
            if (!entry.flagged) {
                entry.suggestedMark = null;
                entry.requestNote = '';
            }
        },
        cancelFlag(index) {
            if (!this.jsonData || !this.jsonData.entries || !this.jsonData.entries[index]) return;
            const entry = this.jsonData.entries[index];
            entry.flagged = false;
            entry.suggestedMark = null;
            entry.requestNote = '';
        },
        postRequest(index) {
            if (!this.jsonData || !this.jsonData.entries || !this.jsonData.entries[index]) return;
            const entry = this.jsonData.entries[index];
            if (!entry.requestNote || !entry.requestNote.trim()) return;
            const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const req = {
                id: `req_${this.nextId++}`,
                author: this.currentUser.name,
                initials: this.currentUser.initials,
                avatarColor: this.currentUser.color,
                time: now,
                isRequest: true,
                resolved: false,
                entryIndex: index,
                word: entry.enteredWord,
                currentMark: entry.selectedAnswer ? entry.selectedAnswer.toUpperCase() : '—',
                suggestedMark: entry.suggestedMark ? entry.suggestedMark.toUpperCase() : null,
                text: entry.requestNote.trim()
            };
            if (!Array.isArray(entry.itemComments)) entry.itemComments = [];
            entry.itemComments.push({ ...req });
            entry.commentCount = entry.itemComments.length;
            this.taskComments.push({ ...req });
            entry.pendingRequest = true;
            entry.flagged = false;
            this.expandedCommentIndex = index;
            entry.requestNote = '';
            this.notifyTaskDataChanged();
        },
        acceptRequest(comment) {
            const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            comment.resolved = true;
            comment.resolvedAction = 'Accepted';
            comment.resolvedBy = this.currentUser.name;

            if (!this.jsonData || !Array.isArray(this.jsonData.entries)) return;
            const entry = this.jsonData.entries[comment.entryIndex];
            if (entry && comment.suggestedMark) {
                const prev = entry.selectedAnswer;
                const next = comment.suggestedMark.toLowerCase();
                entry.selectedAnswer = next;
                if (!Array.isArray(entry.changeLog)) entry.changeLog = [];
                entry.changeLog.push({
                    id: `h_${this.nextId++}`,
                    actor: this.currentUser.name,
                    actorColor: this.currentUser.color,
                    from: prev ? prev.toUpperCase() : '—',
                    to: comment.suggestedMark,
                    reason: `Accepted request from ${comment.author}`,
                    time: now
                });
            }
            if (entry) {
                entry.pendingRequest = false;
                const ic = entry.itemComments.find((x) => x.id === comment.id);
                if (ic) {
                    ic.resolved = true;
                    ic.resolvedAction = 'Accepted';
                }
            }
            this.notifyTaskDataChanged();
        },
        rejectRequest(comment) {
            comment.resolved = true;
            comment.resolvedAction = 'Rejected';
            comment.resolvedBy = this.currentUser.name;
            if (!this.jsonData || !Array.isArray(this.jsonData.entries)) return;
            const entry = this.jsonData.entries[comment.entryIndex];
            if (entry) {
                entry.pendingRequest = false;
                const ic = entry.itemComments.find((x) => x.id === comment.id);
                if (ic) {
                    ic.resolved = true;
                    ic.resolvedAction = 'Rejected';
                }
            }
            this.notifyTaskDataChanged();
        },
        postComment(index) {
            if (!this.jsonData || !this.jsonData.entries || !this.jsonData.entries[index]) return;
            const entry = this.jsonData.entries[index];
            if (!entry.newComment || !entry.newComment.trim()) return;
            const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            if (!Array.isArray(entry.itemComments)) entry.itemComments = [];
            entry.itemComments.push({
                id: `c_${this.nextId++}`,
                author: this.currentUser.name,
                initials: this.currentUser.initials,
                avatarColor: this.currentUser.color,
                time: now,
                isRequest: false,
                text: entry.newComment.trim()
            });
            entry.commentCount = entry.itemComments.length;
            entry.newComment = '';
            this.notifyTaskDataChanged();
        }
    }
};
</script>

<style scoped>
.text-input-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    row-gap: 10px;
    column-gap: 20px;
}

@media (max-width: 1024px) {
    .text-input-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .text-input-grid {
        grid-template-columns: 1fr;
    }

}

.input-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 10px;
    gap: 8px 8px;
}

.input-row-main {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    gap: 0 8px;
}

.item-comment-button {
    position: relative;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    color: #6b7280;
}

.item-comment-button:hover {
    background: #f9fafb;
    border-color: #4a90e2;
    color: #4a90e2;
}

.item-comment-button.has-comments {
    background: #eff6ff;
    border-color: #4a90e2;
    color: #4a90e2;
}

.comment-icon {
    font-size: 16px;
}

.comment-count {
    position: absolute;
    font-size: 10px;
    font-weight: 600;
    color: #fff;
    background: #4a90e2;
    min-width: 16px;
    height: 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    top: -4px;
    right: -4px;
}

.item-comment-panel-wrap {
    flex: 1 1 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
}

.item-comment-panel {
    width: 440px;
    max-width: 100%;
    max-height: 320px;
    overflow: auto;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e7eb;
    padding: 12px;
}

.input-number {
    width: 30px;
    font-weight: bold;
    text-align: right;
    margin-right: 10px;
}

.input-container {
    position: relative;
    flex-grow: 1;
}

.line-input {
    display: flex;
    align-items: center;
    padding: 8px 8px 8px 0;
    margin-bottom: 2px;
    gap: 8px;
    border-bottom: 1px solid #e5e7eb;
    transition: background 0.15s;
}

.line-input.flagged {
    background: #fffbeb;
    border-radius: 4px;
    padding-left: 6px;
}

.word-field {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
}

.suggestion-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    padding: 0;
    color: #777;
    font-style: italic;
    cursor: pointer;
    opacity: 0.9;
    z-index: 1;
    border-radius: 4px;
    pointer-events: none; /* Allow clicks to pass through to the input */
}

.suggestion-text {
    padding: 10px 12px;
    line-height: 1.5;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.note {
    margin-left: 25px;
    margin-bottom: 30px;
}

.transcription-status {
    margin-bottom: 20px;
}

.transcribing-indicator {
    color: #3498db;
    font-style: italic;
}

.transcription-error {
    color: #e74c3c;
    margin-top: 5px;
    margin-bottom: 15px;
    font-size: 14px;
}

.summary-section {
    margin-top: 30px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f9f9f9;
}

.summary-section h3 {
    margin-top: 0;
    color: #237091;
    margin-bottom: 15px;
}

.summary-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}

.summary-item {
    display: flex;
    align-items: center;
    min-width: 150px;
}

.summary-label {
    font-weight: bold;
    margin-right: 10px;
}

.summary-count {
    font-size: 18px;
    color: #237091;
}

.summary-label.open-requests {
    color: #d97706;
}

.summary-count.open-requests {
    color: #d97706;
}

.ai-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: #237091;
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: bold;
    z-index: 2;
}

.history-btn {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 600;
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
    padding: 1px 6px;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
}

.history-btn:hover {
    background: #fde68a;
}

.history-panel {
    margin-top: 4px;
    margin-left: 0;
    padding: 8px 10px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    font-size: 12px;
}

.history-title {
    font-weight: 600;
    color: #92400e;
    margin-bottom: 6px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.history-item {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    flex-wrap: wrap;
}

.history-item:last-child {
    margin-bottom: 0;
}

.history-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.history-actor {
    font-weight: 600;
    color: #374151;
}

.history-change {
    display: flex;
    align-items: center;
    gap: 4px;
}

.mark-pill {
    font-size: 11px;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 4px;
    background: #f1f5f9;
    color: #475569;
}

.mark-pill.C {
    background: #dcfce7;
    color: #166534;
}

.mark-pill.I {
    background: #fee2e2;
    color: #991b1b;
}

.mark-pill.R {
    background: #fef3c7;
    color: #92400e;
}

.mark-pill.Q {
    background: #e0f2fe;
    color: #075985;
}

.history-reason {
    color: #6b7280;
    flex: 1;
    font-style: italic;
}

.history-time {
    color: #9ca3af;
    white-space: nowrap;
}

.flag-button {
    color: #d1d5db;
    font-size: 14px;
}

.flag-button:hover {
    border-color: #f59e0b !important;
    color: #f59e0b !important;
    background: #fffbeb !important;
}

.flag-button.is-flagged {
    border-color: #f59e0b;
    background: #fffbeb;
    color: #d97706;
}

.flag-button.is-posted {
    border-color: #fb923c;
    background: #fff7ed;
    color: #ea580c;
}

.request-form {
    flex: 1 1 100%;
    margin-top: 2px;
    margin-left: 40px;
}

.request-form-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 8px 10px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    font-size: 13px;
}

.request-label {
    color: #78350f;
    font-weight: 500;
    white-space: nowrap;
}

.request-divider {
    color: #d1d5db;
}

.request-input {
    flex: 1;
    min-width: 120px;
    padding: 5px 8px;
    border: 1px solid #fde68a;
    border-radius: 4px;
    font-size: 13px;
    font-family: inherit;
    background: white;
}

.request-input:focus {
    outline: none;
    border-color: #f59e0b;
}

.request-submit {
    background: #d97706;
    color: white;
    border: none;
    padding: 5px 12px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
}

.request-submit:disabled {
    background: #d1d5db;
    cursor: not-allowed;
}

.request-submit:not(:disabled):hover {
    background: #b45309;
}

.request-cancel {
    background: none;
    border: 1px solid #d1d5db;
    color: #6b7280;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
}

.mini-thread-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
}

.mini-close {
    background: none;
    border: none;
    font-size: 12px;
    color: #6b7280;
    cursor: pointer;
}

.mini-empty {
    font-size: 13px;
    color: #9ca3af;
    text-align: center;
    padding: 8px;
}

.mini-comment {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    padding: 8px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: white;
}

.mini-comment.mini-request {
    border-color: #fcd34d;
    background: #fffbeb;
}

.mini-avatar {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: white;
}

.mini-body {
    flex: 1;
    min-width: 0;
}

.mini-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 2px;
    flex-wrap: wrap;
}

.mini-author {
    font-size: 12px;
    font-weight: 600;
    color: #111827;
}

.mini-request-badge {
    font-size: 10px;
    background: #fef3c7;
    color: #92400e;
    padding: 1px 6px;
    border-radius: 8px;
    font-weight: 600;
}

.mini-time {
    font-size: 11px;
    color: #9ca3af;
    margin-left: auto;
}

.mini-text {
    font-size: 12px;
    color: #374151;
}

.mini-marks {
    display: flex;
    gap: 6px;
    margin-top: 4px;
    flex-wrap: wrap;
}

.mark-chip {
    font-size: 11px;
    background: #f1f5f9;
    color: #475569;
    padding: 1px 7px;
    border-radius: 8px;
}

.mark-chip.suggested {
    background: #dcfce7;
    color: #166534;
}

.mark-chip.resolved {
    background: #e0f2fe;
    color: #075985;
}

.mini-new {
    padding-top: 8px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 6px;
    align-items: flex-end;
}

.mini-input {
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 12px;
    font-family: inherit;
    resize: none;
}

.mini-input:focus {
    outline: none;
    border-color: #4a90e2;
}

.mini-submit {
    background: #4a90e2;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
}

.mini-submit:disabled {
    background: #d1d5db;
    cursor: not-allowed;
}

.accept-btn {
    font-size: 12px;
    font-weight: 600;
    color: #166534;
    background: #dcfce7;
    border: 1px solid #86efac;
    padding: 3px 12px;
    border-radius: 10px;
    cursor: pointer;
}

.accept-btn:hover {
    background: #bbf7d0;
}

.reject-btn {
    font-size: 12px;
    font-weight: 600;
    color: #991b1b;
    background: #fee2e2;
    border: 1px solid #fca5a5;
    padding: 3px 12px;
    border-radius: 10px;
    cursor: pointer;
}

.reject-btn:hover {
    background: #fecaca;
}

/* Four-way toggle buttons used in "Suggest change to:" */
.toggle {
    display: flex;
    justify-content: space-between;
    width: 100px;
    border: 1px solid #3f8fb2;
    border-radius: 10px;
    overflow: hidden;
    flex-shrink: 0;
}

.toggle.small {
    width: 88px;
}

.option {
    flex: 1;
    text-align: center;
    line-height: 30px;
    cursor: pointer;
    background-color: white;
    color: #3f8fb2;
    border-right: 1px solid #3f8fb2;
    font-size: 13px;
    transition: background-color 0.3s ease;
}

.option:last-child {
    border-right: none;
}

.option.active {
    background-color: #4aa7d0;
    background-image: linear-gradient(to bottom, #4aa7d0, #357a96);
    color: white;
}
</style>
